import jobServices from 'ApiService/Placement/jobServices';
import Card from 'components/card';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { BallTriangle } from 'react-loader-spinner';
import EditTestModal from './EditModel';

const ViewMockMarks = ({ classData, handleBack }) => {
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [mockTestLinks, setMockTestLinks] = useState([]);
  const [fetchingLinks, setFetchingLinks] = useState(false);
  const [editingTest, setEditingTest] = useState(null);

  const getStudents = async () => {
    try {
      const response = await jobServices.getPlacementReadyStudent(
        classData.courseId,
        classData.batchId
      );
      if (response.data.success) {
        setStudents(
          response.data.data.map((student) => ({
            id: student._id,
            name: student.name,
            instituteId: student.instituteId,
          }))
        );
      }
    } catch (error) {
      toast.error('Failed to fetch students');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    getStudents();
  }, [classData]);
  const fetchMockTestLinks = async (student) => {
    setFetchingLinks(true);
    setSelectedStudent(student);

    try {
      const response = await jobServices.getMockTestLinks({
        courseId: classData.courseId,
        batchId: classData.batchId,
        studentId: student.id,
      });

      if (response.data.success) {
        setMockTestLinks(response.data.data);
      } else {
        setMockTestLinks([]);
      }
    } catch (error) {
      toast.error('Failed to fetch test links');
    } finally {
      setFetchingLinks(false);
    }
  };

  // Handle Delete Test Link
  const handleDelete = async (mockTestId) => {
    if (!window.confirm('Are you sure you want to delete this test link?'))
      return;

    try {
      const response = await jobServices.deleteMockTestLink(mockTestId);
      if (response.data.success) {
        toast.success('Test link deleted successfully');
        setMockTestLinks((prev) =>
          prev.filter((test) => test._id !== mockTestId)
        );
      } else {
        toast.error('Failed to delete test link');
      }
    } catch (error) {
      toast.error('Error deleting test link');
    }
  };

  // Handle Save Update
  const handleSaveUpdate = async () => {
    try {
      const response = await jobServices.updateMockTestLink({
        mockTestId: editingTest._id,
        testDate: editingTest.testDate,
        testTime: editingTest.testTime,
        testLink: editingTest.testLink,
        status: editingTest.status,
      });

      if (response.data.success) {
        toast.success('Test updated successfully');
        setMockTestLinks((prev) =>
          prev.map((test) =>
            test._id === editingTest._id ? { ...test, ...editingTest } : test
          )
        );
        setEditingTest(null);
      } else {
        toast.error('Failed to update test link');
      }
    } catch (error) {
      toast.error('Error updating test link');
    }
  };

  if (loading) {
    return (
      <div className="flex h-40 items-center justify-center">
        <BallTriangle color="#4fa94d" height={80} width={80} />
      </div>
    );
  }

  return (
    <Card>
      <h2 className="mb-4 mt-4 text-lg font-bold">
        {selectedStudent
          ? `Mock Test Details - ${selectedStudent.name}`
          : 'Mock Test Links'}
      </h2>

      {selectedStudent ? (
        <div>
          <h3 className="text-md font-semibold">
            Test Links for {selectedStudent.name}
          </h3>

          {fetchingLinks ? (
            <div className="flex h-20 items-center justify-center">
              <BallTriangle color="#4fa94d" height={50} width={50} />
            </div>
          ) : mockTestLinks.length ? (
            <table className="mt-4 w-full border-collapse border border-gray-700">
              <thead>
                <tr className="bg-gray-100 dark:bg-navy-800">
                  <th className="border p-2">Subject</th>
                  <th className="border p-2">Test Date</th>
                  <th className="border p-2">Test Time</th>
                  <th className="border p-2">Test Link</th>
                  <th className="border p-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {mockTestLinks.map((test) => (
                  <tr key={test._id} className="border">
                    <td className="border p-2">{test.subjectName}</td>
                    <td className="border p-2">
                      {new Date(test.testDate).toLocaleDateString()}
                    </td>
                    <td className="border p-2">{test.testTime || 'N/A'}</td>
                    <td className="border p-2">
                      <a
                        href={test.testLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 underline"
                      >
                        Open Link
                      </a>
                    </td>
                    <td className="border p-2 text-center">
                      <button
                        className="mr-2 rounded bg-yellow-500 px-3 py-1 text-white"
                        onClick={() => setEditingTest(test)}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(test._id)}
                        className="rounded bg-red-500 px-3 py-1 text-white"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-gray-500">No test links available</p>
          )}

          <div className="flex flex-col justify-between px-3 pt-2 md:flex-row md:px-8">
            <button
              onClick={() => {
                setSelectedStudent(null);
                setMockTestLinks([]);
              }}
              className="mb-3 cursor-pointer rounded-xl bg-lightPrimary px-16 py-2 font-medium text-navy-700 transition duration-200 dark:bg-white/5 dark:text-white dark:hover:bg-white/10 dark:active:bg-white/20 md:mb-0"
            >
              Back to List
            </button>
          </div>
        </div>
      ) : (
        <div>
          {students?.length > 0 ? (
            students?.map((student, index) => (
              <table className="w-full border-collapse border border-gray-700">
                <thead>
                  <tr className="bg-gray-100 dark:bg-navy-700 ">
                    <th className="border p-2">SR.NO</th>
                    <th className="border p-2">Name</th>
                    <th className="border p-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    key={student.id}
                    className="cursor-pointer border hover:bg-gray-100 dark:hover:bg-navy-700"
                    onClick={() => fetchMockTestLinks(student)}
                  >
                    <td className="border p-2 text-center">{index + 1}</td>
                    <td className="border p-2">{student.name}</td>
                    <td className="border p-2 text-center">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          fetchMockTestLinks(student);
                        }}
                        className="rounded bg-blue-500 px-3 py-1 text-white"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            ))
          ) : (
            <>
              <p className="text-center text-gray-500">Not available</p>
            </>
          )}
        </div>
      )}

      {editingTest && (
        <EditTestModal
          editingTest={editingTest}
          setEditingTest={setEditingTest}
          handleSaveUpdate={handleSaveUpdate}
          onCancel={() => setEditingTest(null)}
        />
      )}

      {!selectedStudent && (
        <div className="flex flex-col justify-between px-3 pt-2 md:flex-row md:px-8">
          <button
            onClick={handleBack}
            className="mb-3 cursor-pointer rounded-xl bg-lightPrimary px-16 py-2 font-medium text-navy-700 transition duration-200 dark:bg-white/5 dark:text-white dark:hover:bg-white/10 dark:active:bg-white/20 md:mb-0"
          >
            Back
          </button>
        </div>
      )}
    </Card>
  );
};

export default ViewMockMarks;
